import { useState } from "react";
import { Headphones, Music2, TrendingUp, User, Bell, CreditCard, Settings, Search, Plus, Send } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal, MaskLine } from "../components/Reveal";

const FAQ_DATA = [
  {
    category: "Sessions",
    icon: Headphones,
    items: [
      { q: "What does it mean to host a session on Tap In?", a: "Hosting a session allows you to stream music in real time with others. All participants hear the same song at the same moment, creating a shared listening experience." },
      { q: "How does the music sync work?", a: "Tap In provides 100 percent audio synchronization. When the host plays, pauses, skips, rewinds, or fast-forwards a song, all guests hear the change instantly." },
      { q: "Who controls the music during a session?", a: "The host controls the session by default. The currently playing user is clearly identified and visually illuminated for all guests." },
      { q: "Can guests add songs during a session?", a: "Yes, if the host enables this option. Hosts can allow or block guests from adding songs under the 'Manage Session' settings." },
      { q: "Can I remove songs or users from a session?", a: "Yes. Hosts can delete songs, remove users, or end the session at any time." },
      { q: "Can I invite people to a session?", a: "Yes. Hosts can invite users directly from their contacts or via search and QR code." },
      { q: "Can I end the session at any time?", a: "Yes. Ending the session immediately disconnects all participants." },
      { q: "What happens when I join a session?", a: "You hear exactly what the host is listening to in real time with full audio synchronization." },
      { q: "Can I add songs while in a session?", a: "You can add songs only if the host has enabled guest song additions." },
      { q: "Can I leave and rejoin a session?", a: "Yes. Users may exit and rejoin a session at any time, as long as the session is still active." },
      { q: "Can I react during a session?", a: "Yes. Guests can react to sessions using the fire emoji to show engagement." },
      { q: "Can I decline a session invite?", a: "Yes. All session invites can be accepted or declined." },
    ],
  },
  {
    category: "Playlists",
    icon: Music2,
    items: [
      { q: "What is a Tap In playlist?", a: "Tap In playlists function as memory capsules. They combine music, photos, and videos tied to a moment, experience, or shared memory." },
      { q: "What can I add to a playlist?", a: "You can add songs, photos, and videos to create a fully immersive experience." },
      { q: "Can I add people to my playlist?", a: "Yes. Playlist creators can invite guests directly from contacts, search, or via QR code." },
      { q: "Can guests contribute to playlists?", a: "Yes. Guests can upload photos, videos, and add songs if permitted by the host." },
      { q: "Can the host remove content from a playlist?", a: "Yes. Hosts can delete songs, photos, or videos at any time." },
      { q: "What happens when I join a playlist?", a: "Once added, you can tap on a song and listen in sequential order at your own pace. Playlist listening is not synced in real time." },
      { q: "Can I upload photos and videos to a playlist?", a: "Yes, if the host allows guest contributions." },
      { q: "Can I react to playlists?", a: "Yes. Guests can react to playlists using the fire emoji." },
      { q: "Can I decline a playlist invite?", a: "Yes. Playlist invites can be accepted or declined." },
    ],
  },
  {
    category: "Engagement & Discovery",
    icon: TrendingUp,
    items: [
      { q: "What are Trending Playlists?", a: "Trending Playlists are playlists that receive the highest engagement based on fire emoji reactions." },
      { q: "Can I search for users, songs, or playlists?", a: "Yes. Tap In allows users to search for other users, songs, and playlists." },
      { q: "Can I follow other users?", a: "Yes. Users can follow and unfollow others at any time." },
    ],
  },
  {
    category: "Profiles & Social Features",
    icon: User,
    items: [
      { q: "What can I customize on my profile?", a: "Profiles include a bio, social media links, music genre preferences, and favorite artists." },
      { q: "Can I connect my social media accounts?", a: "Yes. Social media syncing is available within the profile settings." },
      { q: "What is the geo-tag feature?", a: "Users can optionally display their City to show where they are listening to music." },
    ],
  },
  {
    category: "Notifications & Access",
    icon: Bell,
    items: [
      { q: "Does Tap In send notifications?", a: "Yes. Push notifications are available for session invites, playlist invites, activity, and engagement." },
      { q: "Can I join sessions or playlists using a QR code?", a: "Yes. QR codes can be used to instantly join sessions and playlists." },
    ],
  },
  {
    category: "Subscriptions",
    icon: CreditCard,
    items: [
      { q: "Do I need a subscription to use Tap In?", a: "Tap In offers both free and premium features. Some advanced capabilities may require a subscription." },
      { q: "What does Tap In Premium include?", a: "Premium features include enhanced hosting tools, and unlimited playlist creation." },
    ],
  },
  {
    category: "Account Management",
    icon: Settings,
    items: [
      { q: "Can I close my Tap In account?", a: "Yes. Users can close their account at any time through account settings." },
      { q: "What happens when I close my account?", a: "Your profile and associated data will no longer be accessible after account closure, in accordance with Tap In's data policies." },
    ],
  },
];

const CHIPS = [
  { label: "All", match: null },
  { label: "Sessions", match: ["Sessions"] },
  { label: "Playlists", match: ["Playlists"] },
  { label: "Discovery", match: ["Engagement & Discovery"] },
  { label: "Social", match: ["Profiles & Social Features"] },
  { label: "Access", match: ["Notifications & Access"] },
  { label: "Subscriptions", match: ["Subscriptions"] },
  { label: "Account", match: ["Account Management"] },
];

function FaqItem({ item, index, open, onToggle }) {
  return (
    <div className="glass rounded-2xl overflow-hidden" data-testid={`faq-item-${index}`}>
      <button
        type="button"
        data-testid={`faq-question-${index}`}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className="font-semibold text-[0.95rem]">{item.q}</span>
        <motion.span animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.3 }} className="text-grape-soft flex-none">
          <Plus size={18} />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className="px-6 pb-6 text-[0.92rem] leading-relaxed text-dusk">{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ReportForm() {
  const [form, setForm] = useState({ email: "", subject: "", description: "" });
  const [state, setState] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    setState("sending");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/support/ticket`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      setState("done");
      setForm({ email: "", subject: "", description: "" });
    } catch {
      setState("error");
    }
  };

  const field =
    "w-full rounded-2xl border border-grape/25 bg-transparent px-4 py-3 text-[0.95rem] outline-none transition-colors focus:border-grape/60";

  return (
    <form onSubmit={submit} className="mt-8 space-y-4 text-left" data-testid="report-form">
      <div>
        <label htmlFor="report-email" className="block text-sm font-semibold mb-1.5">
          Email
        </label>
        <input
          id="report-email"
          type="email"
          required
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="you@example.com"
          className={field}
          data-testid="report-email"
        />
      </div>
      <div>
        <label htmlFor="report-subject" className="block text-sm font-semibold mb-1.5">
          Subject
        </label>
        <input
          id="report-subject"
          type="text"
          required
          value={form.subject}
          onChange={(e) => setForm({ ...form, subject: e.target.value })}
          placeholder="Brief description of the issue"
          className={field}
          data-testid="report-subject"
        />
      </div>
      <div>
        <label htmlFor="report-description" className="block text-sm font-semibold mb-1.5">
          Description
        </label>
        <textarea
          id="report-description"
          required
          rows={4}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Please provide as much detail as possible…"
          className={`${field} resize-none`}
          data-testid="report-description"
        />
      </div>
      <button type="submit" disabled={state === "sending"} data-testid="report-submit" className="btn-primary w-full justify-center">
        <Send size={17} /> {state === "sending" ? "Sending…" : "Submit Ticket"}
      </button>
      {state === "done" && (
        <p className="text-sm text-center font-medium text-grape-soft" data-testid="report-success">
          Ticket submitted — we'll get back to you soon.
        </p>
      )}
      {state === "error" && (
        <p className="text-sm text-center text-red-400" data-testid="report-error">
          Something went wrong — please try again.
        </p>
      )}
    </form>
  );
}

export default function Faq() {
  const [cat, setCat] = useState("All");
  const [query, setQuery] = useState("");
  const [openKey, setOpenKey] = useState("0-0");

  const q = query.trim().toLowerCase();
  const activeChip = CHIPS.find((c) => c.label === cat);
  const groups = FAQ_DATA.map((g, gi) => ({
    ...g,
    gi,
    items: g.items
      .map((item, ii) => ({ ...item, ii }))
      .filter((item) => !activeChip?.match || activeChip.match.includes(g.category))
      .filter((item) => !q || `${item.q} ${item.a}`.toLowerCase().includes(q)),
  })).filter((g) => g.items.length > 0);

  return (
    <>
      <section className="relative overflow-hidden pt-32 sm:pt-40 pb-10 text-center" data-testid="faq-hero">
        <div className="glow-blob" style={{ width: 560, height: 560, top: "-26%", left: "50%", transform: "translateX(-50%)" }} aria-hidden="true" />
        <div className="relative mx-auto max-w-3xl px-5 sm:px-8">
          <h1 className="hero-title !text-[clamp(2rem,4.6vw,3.4rem)]" data-testid="faq-title">
            <MaskLine delay={0.05}>
              How Can We <span className="text-gradient">Help?</span>
            </MaskLine>
          </h1>
          <p className="body-lg mt-5">Everything you need to know about Tap In</p>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="relative mt-8"
            data-testid="faq-search-wrap"
          >
            <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-dusk" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search for answers…"
              data-testid="faq-search"
              className="w-full rounded-full glass px-11 py-3.5 text-[0.95rem] outline-none border border-grape/25 focus:border-grape/50"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="mt-7 flex flex-nowrap overflow-x-auto no-scrollbar justify-start sm:justify-center gap-2 pb-2"
            data-testid="faq-chips"
          >
            {CHIPS.map((c) => {
              const isActive = cat === c.label;
              return (
                <button
                  key={c.label}
                  type="button"
                  onClick={() => setCat(c.label)}
                  data-testid={`faq-chip-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`inline-flex items-center whitespace-nowrap rounded-full px-3 py-1.5 text-[0.8rem] font-medium transition-all duration-300 flex-none ${
                    isActive
                      ? "bg-gradient-to-r from-[#8b4dff] to-[#4d7cff] text-white shadow-glow"
                      : "glass text-chalk/80 hover:text-chalk"
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="pb-20" data-testid="faq-list">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          {groups.length === 0 && (
            <p className="text-center body-lg py-14" data-testid="faq-empty">
              No answers match &ldquo;{query}&rdquo; — try another word.
            </p>
          )}
          {groups.map((g) => (
            <div key={g.category} className="mb-12" data-testid={`faq-group-${g.gi}`}>
              <Reveal>
                <h2 className="font-display text-xl sm:text-2xl font-semibold">{g.category}</h2>
              </Reveal>
              <div className="mt-5 space-y-3.5">
                {g.items.map((item) => {
                  const key = `${g.gi}-${item.ii}`;
                  return (
                    <Reveal key={key} delay={item.ii * 0.03}>
                      <FaqItem item={item} index={item.ii} open={openKey === key} onToggle={() => setOpenKey(openKey === key ? "" : key)} />
                    </Reveal>
                  );
                })}
              </div>
            </div>
          ))}

          <Reveal>
            <div className="cta-panel rounded-3xl border border-grape/25 px-6 py-10 text-center mt-14 sm:px-10" data-testid="faq-cta">
              <h2 className="font-display text-2xl">Still have questions?</h2>
              <p className="body-lg mt-2.5 text-[0.95rem]">
                Can't find what you're looking for? Submit a ticket and we'll get back to you.
              </p>
              <ReportForm />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
